import { Button } from '@components/button';
import { Group } from '@components/layout';
import { useModal } from '@components/modal';
import { Typography } from '@components/typography';
import { useGetPreviewQuery } from '@generated/graphql';
import { useTheme } from '@mui/material';
import { useCurrentWorkspaceId } from '@navigation/WorkspaceContext';
import { useNotifications } from '@notifications/Notifications';
import { navigate } from 'gatsby';
import posthog from 'posthog-js';
import { useCallback, useContext, useEffect, useState } from 'react';
import { GraphsListViewPreviewMenuDuplicateConfiguration } from '../GraphsListViewPreviewMenuDuplicateConfiguration';
import { GraphConfigStageButton } from './GraphConfigStageButton';
import { GraphDataContext } from './GraphDataProvider';
import { GraphPreviewModal } from './GraphPreviewModal';

export const GraphHeader = () => {
  const { palette } = useTheme();
  const workspaceId = useCurrentWorkspaceId();
  const { graphMetadata, graphParseError, refetchGraphMetadata } = useContext(GraphDataContext);
  const disabled = graphMetadata ? graphMetadata.readOnly : true;
  const { addNotification, removeNotification } = useNotifications();

  const [previewId, setPreviewId] = useState<string>();
  const [openPreviewModal, setOpenPreviewModal] = useState(false);
  const handleClosePreview = useCallback(() => setOpenPreviewModal(false), []);

  const onDuplicate = (newGraphId?: string) => {
    if (newGraphId) {
      navigate(`/workspaces/${workspaceId}/graphs/`);
    } else {
      refetchGraphMetadata();
    }
  };

  const { showModal: showDuplicateModal } = useModal({
    component: GraphsListViewPreviewMenuDuplicateConfiguration,
    componentProps: { ...graphMetadata, refetch: onDuplicate },
    modalProps: { title: `Save ${graphMetadata?.name || 'graph'} copy as` },
  });

  const preview = useGetPreviewQuery({
    variables: { previewId, workspaceId },
    skip: !previewId,
    pollInterval: 3000,
  });
  const { refetch: refetchPreview, startPolling, stopPolling } = preview;

  useEffect(() => {
    stopPolling();
    if (!previewId) {
      return;
    }
    refetchPreview({ previewId })
      .then(() => {
        startPolling(3000);
      })
      .catch((e) => console.error(e));
  }, [previewId, refetchPreview, startPolling, stopPolling]);

  const previewStatus = preview?.data?.getPreview?.status;

  useEffect(() => {
    removeNotification('PREVIEW_STATUS');
    if (!previewStatus) {
      return;
    }
    if (['succeeded', 'success', 'complete'].includes(previewStatus)) {
      addNotification({
        key: 'PREVIEW_STATUS',
        type: 'success',
        text: 'Preview generation complete',
        duration: 3000,
      });
    } else if (['failed', 'timeout'].includes(previewStatus)) {
      addNotification({
        key: 'PREVIEW_STATUS',
        type: 'error',
        text: 'Preview generation failed',
        duration: 5000,
      });
    } else {
      addNotification({
        key: 'PREVIEW_STATUS',
        type: 'loading',
        text: `Generating preview: ${previewStatus}`,
        duration: 600000,
      });
    }
  }, [previewStatus, addNotification, removeNotification]);

  useEffect(() => {
    return () => {
      removeNotification('PREVIEW_STATUS');
    };
  }, [removeNotification]);

  if (['failed', 'success', 'timeout'].includes(previewStatus)) {
    preview?.stopPolling();
  }

  return (
    <Group alignItems="center" gap={4}>
      {disabled && (
        <Typography variant="subtitle1" color={palette.common.white}>
          Readonly
        </Typography>
      )}
      {!graphParseError && (
        <Group spacing={4} alignItems="center">
          {disabled ? (
            <Button
              size="small"
              onClick={() => {
                posthog.capture('user_copy-graph_intent');
                showDuplicateModal();
              }}
            >
              Save as and edit
            </Button>
          ) : (
            <>
              <Button
                data-cy="Graph-Config-Preview-Button"
                size="small"
                onClick={() => setOpenPreviewModal(true)}
              >
                Preview
              </Button>
              <GraphConfigStageButton />
            </>
          )}
        </Group>
      )}
      <GraphPreviewModal
        open={openPreviewModal}
        onClose={handleClosePreview}
        graphId={graphMetadata?.graphId}
        graphName={graphMetadata?.name}
        previewId={previewId}
        setPreviewId={setPreviewId}
        preview={preview?.data?.getPreview}
      />
    </Group>
  );
};
